import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    service_category: '',
    additional_info: ''
  });

  const segurosOptions = [
    { value: 'vida', label: 'Seguros de Vida' },
    { value: 'hogar', label: 'Seguros de Hogar' },
    { value: 'arrendamiento', label: 'Seguros de Arrendamiento' },
    { value: 'mascotas', label: 'Seguros de Mascotas' },
    { value: 'autos', label: 'Seguros de Autos' },
    { value: 'cumplimiento', label: 'Seguros de Cumplimiento' },
    { value: 'soat', label: 'SOAT' },
    { value: 'otros', label: 'Otros Seguros' }
  ];

  const creditosOptions = [
    { value: 'vehicular', label: 'Crédito Vehicular' },
    { value: 'hipotecario', label: 'Crédito Hipotecario' },
    { value: 'libre_inversion', label: 'Crédito Libre Inversión' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset service_category when service_type changes
      ...(name === 'service_type' ? { service_category: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.service_category) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/quotes`, formData);
      
      if (response.status === 200) {
        toast.success('¡Solicitud enviada exitosamente! Nos contactaremos contigo pronto.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: '',
          service_category: '',
          additional_info: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const currentOptions = formData.service_type === 'seguros' ? segurosOptions : 
                        formData.service_type === 'creditos' ? creditosOptions : [];

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-xl border-none">
      <CardHeader className="bg-gradient-to-r from-[#1565c0]/5 to-[#00c853]/5">
        <CardTitle className="text-2xl text-[#0d1b2a]">Solicita tu Cotización</CardTitle>
        <CardDescription className="text-base">
          Completa el formulario y nos contactaremos contigo en menos de 24 horas
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#0d1b2a] font-medium">
                Nombre Completo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Juan Pérez"
                required
                className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#0d1b2a] font-medium">
                Teléfono <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="3001234567"
                required
                className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#0d1b2a] font-medium">
              Correo Electrónico <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="correo@ejemplo.com"
              required
              className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="service_type" className="text-[#0d1b2a] font-medium">
              Tipo de Servicio <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.service_type} 
              onValueChange={(value) => handleSelectChange('service_type', value)}
              required
            >
              <SelectTrigger className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0]">
                <SelectValue placeholder="Selecciona el tipo de servicio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seguros">Seguros</SelectItem>
                <SelectItem value="creditos">Créditos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.service_type && (
            <div className="space-y-2">
              <Label htmlFor="service_category" className="text-[#0d1b2a] font-medium">
                {formData.service_type === 'seguros' ? 'Tipo de Seguro' : 'Tipo de Crédito'} <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.service_category} 
                onValueChange={(value) => handleSelectChange('service_category', value)}
                required
              >
                <SelectTrigger className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0]">
                  <SelectValue placeholder={`Selecciona ${formData.service_type === 'seguros' ? 'el seguro' : 'el crédito'}`} />
                </SelectTrigger>
                <SelectContent>
                  {currentOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="additional_info" className="text-[#0d1b2a] font-medium">
              Información Adicional (Opcional)
            </Label>
            <Textarea
              id="additional_info"
              name="additional_info"
              value={formData.additional_info}
              onChange={handleChange}
              placeholder="Cuéntanos más sobre lo que necesitas..."
              rows={4}
              className="border-gray-300 focus:border-[#1565c0] focus:ring-[#1565c0] resize-none"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1565c0] to-[#00c853] hover:from-[#1e88e5] hover:to-[#43a047] text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {loading ? 'Enviando...' : 'Solicitar Cotización'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuoteForm;
